import CryptoJS from 'crypto-js';

import { inject, Injectable } from '@angular/core';
import { KeychainAccess, SecureStorage } from '@aparajita/capacitor-secure-storage';
import { Capacitor } from '@capacitor/core';
import { StorageService } from '@movilidad/app-core';
import * as Sentry from '@sentry/angular';

/** Lo que se guarda de una tarjeta. El CVC nunca. */
export interface SavedCard {
  fullname: string;
  creditCard: string;
  expirationDate: string;
}

const SECURE_KEY_PREFIX = 'movilidad-card_';
const LEGACY_KEY_PREFIX = 'pInfo-';
/**
 * Llave fija con la que hasta la 4.38 se cifraba la tarjeta en Preferences. Ya era pública (venía
 * en el bundle); queda SÓLO para leer y mover lo que dejaron esas versiones, nunca para escribir.
 * Se borra junto con crypto-js cuando el piso de force-update deje atrás la 4.38.
 */
const LEGACY_SECURE_KEY = '17749937251153819552391078335260';

/**
 * Tarjeta guardada en el dispositivo (auditoría de seguridad 2026-10).
 *
 * Vive en el almacenamiento seguro del sistema: Keychain en iOS (sólo este dispositivo, sin
 * iCloud ni respaldos) y Keystore en Android (AES-GCM con llave del Keystore; el app ya tiene
 * allowBackup=false). Cada usuario tiene su propia entrada, así que otro usuario del mismo
 * teléfono no la ve, y no se borra al cerrar sesión porque el app también cierra sesión sola
 * (token vencido, SSO) y la gente perdería su tarjeta sin haber hecho nada.
 *
 * En web no hay almacenamiento seguro (el plugin escribiría en localStorage en claro), así que
 * ahí no se guarda nada.
 */
@Injectable({ providedIn: 'root' })
export class SavedCardService {
  #storage = inject(StorageService);

  readonly supported = Capacitor.isNativePlatform();

  #configured: Promise<void> | null = null;
  #migrated: Promise<void> | null = null;

  async get(userId: number | string): Promise<SavedCard | null> {
    if (!this.supported) return null;
    await this.migrateLegacy();
    try {
      await this.#configure();
      const value = await SecureStorage.get(this.#key(userId), false);
      if (value == null) return null;
      if (isSavedCard(value)) return value;
      await SecureStorage.remove(this.#key(userId));
      return null;
    } catch (error) {
      this.#report('get', error);
      return null;
    }
  }

  async save(userId: number | string, card: SavedCard): Promise<void> {
    if (!this.supported) return;
    try {
      await this.#configure();
      await this.#write(userId, card);
    } catch (error) {
      this.#report('save', error);
    }
  }

  async remove(userId: number | string): Promise<void> {
    if (!this.supported) return;
    try {
      await this.#configure();
      await SecureStorage.remove(this.#key(userId));
    } catch (error) {
      this.#report('remove', error);
    }
  }

  /**
   * Mueve al almacenamiento seguro las tarjetas que dejaron las versiones anteriores (Preferences
   * con la llave fija) y borra el original. Corre una sola vez por arranque, para todos los
   * usuarios que hayan guardado tarjeta en este teléfono. Si la escritura segura falla, el original
   * se queda para reintentar en el siguiente arranque en vez de perder la tarjeta.
   */
  migrateLegacy(): Promise<void> {
    this.#migrated ??= this.#migrateLegacy();
    return this.#migrated;
  }

  async #migrateLegacy(): Promise<void> {
    let keys: string[];
    try {
      keys = await this.#storage.keysWithPrefix(LEGACY_KEY_PREFIX);
    } catch (error) {
      this.#report('migrate', error);
      return;
    }

    for (const legacyKey of keys) {
      const userId = legacyKey.slice(LEGACY_KEY_PREFIX.length);
      try {
        // Sin usuario (se guardó con la sesión a medio cargar) no hay a quién devolvérsela.
        const card = this.supported && /^\d+$/.test(userId) ? await this.#readLegacy(legacyKey) : null;
        if (card) {
          await this.#configure();
          // Si ya hay una tarjeta segura para ese usuario es más reciente que la vieja: gana.
          if ((await SecureStorage.get(this.#key(userId), false)) == null) {
            await this.#write(userId, card);
          }
        }
        await this.#storage.remove(legacyKey);
      } catch (error) {
        this.#report('migrate', error);
      }
    }
  }

  async #readLegacy(legacyKey: string): Promise<SavedCard | null> {
    const encrypted = await this.#storage.get<string>(legacyKey);
    if (typeof encrypted !== 'string' || !encrypted) return null;
    try {
      const decrypted = CryptoJS.AES.decrypt(encrypted, LEGACY_SECURE_KEY).toString(CryptoJS.enc.Utf8);
      const card = decrypted ? JSON.parse(decrypted) : null;
      return isSavedCard(card) ? pick(card) : null;
    } catch {
      // Ilegible (formato ajeno, restaurado de otro lado): se descarta igual que antes.
      return null;
    }
  }

  async #write(userId: number | string, card: SavedCard): Promise<void> {
    await SecureStorage.set(this.#key(userId), pick(card), false, false, KeychainAccess.whenUnlockedThisDeviceOnly);
  }

  /** Prefijo propio y sin iCloud; una vez por arranque. */
  #configure(): Promise<void> {
    this.#configured ??= (async () => {
      await SecureStorage.setKeyPrefix(SECURE_KEY_PREFIX);
      await SecureStorage.setSynchronize(false);
      await SecureStorage.setDefaultKeychainAccess(KeychainAccess.whenUnlockedThisDeviceOnly);
    })().catch((error) => {
      this.#configured = null;
      throw error;
    });
    return this.#configured;
  }

  #key(userId: number | string): string {
    return String(userId);
  }

  /** Nunca datos de la tarjeta en Sentry: sólo la operación y el código del error. */
  #report(operation: string, error: unknown): void {
    const code = (error as { code?: string } | null)?.code ?? 'unknown';
    Sentry.captureMessage(`saved-card: ${operation} failed (${code})`, {
      level: 'warning',
      tags: { flow: 'saved-card', operation, code },
    });
  }
}

function isSavedCard(value: unknown): value is SavedCard {
  const card = value as Partial<SavedCard> | null;
  return (
    !!card &&
    typeof card === 'object' &&
    typeof card.fullname === 'string' &&
    typeof card.creditCard === 'string' &&
    typeof card.expirationDate === 'string' &&
    !!card.creditCard
  );
}

/** Sólo los tres campos, por si algo más viajó en el objeto. */
function pick(card: SavedCard): SavedCard & Record<string, unknown> {
  return { fullname: card.fullname, creditCard: card.creditCard, expirationDate: card.expirationDate };
}
